import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

// Shapes
import LinkShape from '../../../lib/shapes/LinkShape';

// Components
import MemberDescription from './partials/MemberDescription';
import { BlockInteractions, createButtonObject, DISPLAY_TYPE, Views } from '../../organisms/BlockInteractions';
import { Icons } from '../../atoms/SVG';
import BlockFetcher from '../../hocs/BlockFetcher/BlockFetcher';
import { TeamroomInviteView } from '../../forms/TeamroomInvite/TeamroomInvite';

import { Namespace } from '../../../translation/namespace';
import { RequestActivityForm } from './forms/RequestActivityForm';

/**
 * using two independent instances of TeamroomInvite to avoid react reusing select with wrong initial value
 * (compare SIBZIEL-9325)
 * */
// TODO:
// const InviteRegisteredUserView = BlockFetcher()(TeamroomInvite);
// const InviteUnRegisteredUserView = BlockFetcher()(TeamroomInvite);
const RequestActivationView = BlockFetcher()(RequestActivityForm);
const AcceptActivationView = BlockFetcher()(RequestActivityForm);
const RejectActivationView = BlockFetcher()(RequestActivityForm);

/**
 * Renders a bar with Buttons to manipulate a single Teamroom
 */
export function TeamroomManagement({
  uuid,
  elementType,
  profileLink,
  profileLinkActivationRequester = null,
  roles = null,
  teamroomMembershipType = null,
  readRestricted = false,
  activeView = null,
  views = null,
  refresh,
  refreshCount,
}) {
  const { t } = useTranslation(Namespace.TeamroomManagement);
  const translationNamespace = Namespace.TeamroomManagement;

  const getHeadline = useCallback(() => {
    return {
      text: t('headline'),
      hierarchy: 2,
      visible: false,
    };
    // useCallback makes sense here, b/c it's a pure function.
    // And I don't care if `t` gets recreated / a new reference in memory.
    // ...this may change though - in a galaxy far far away - when some
    // brave user would change the language, maybe ¯\_(ツ)_/¯
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderMemberDescription = () => {
    return (
      <MemberDescription
        roles={roles}
        membershipType={teamroomMembershipType}
        profileLink={profileLink}
        profileLinkActivationRequester={profileLinkActivationRequester}
      />
    );
  };

  const createButtonsObject = () => {
    const AcceptActivationButton =
      views[Views.ACCEPT_ACTIVATION] &&
      createButtonObject(
        Views.ACCEPT_ACTIVATION,
        Icons.ACCEPT_CHECK,
        `${translationNamespace}:actions.acceptActivation`,
        DISPLAY_TYPE.MODAL,
        <AcceptActivationView
          additionalProps={{
            translationNamespace: `${Namespace.TeamroomManagement}:actions.acceptActivation`,
            refreshBlock: refresh,
            readRestricted,
          }}
          {...views[Views.ACCEPT_ACTIVATION]}
        />,
        null,
        true,
      );

    const RejectActivationButton =
      views[Views.REJECT_ACTIVATION] &&
      createButtonObject(
        Views.REJECT_ACTIVATION,
        Icons.DECLINE_CROSS,
        `${translationNamespace}:actions.rejectActivation`,
        DISPLAY_TYPE.MODAL,
        <RejectActivationView
          additionalProps={{
            translationNamespace: `${Namespace.TeamroomManagement}:actions.rejectActivation`,
            refreshBlock: refresh,
            readRestricted,
          }}
          {...views[Views.REJECT_ACTIVATION]}
        />,
        null,
        true,
      );

    const RequestActivationButton =
      views[Views.REQUEST_ACTIVATION] &&
      createButtonObject(
        Views.REQUEST_ACTIVATION,
        undefined,
        `${translationNamespace}:actions.requestActivation`,
        DISPLAY_TYPE.MODAL,
        views[Views.REQUEST_ACTIVATION]?.embedded?.form
          ? (
              <RequestActivationView
                additionalProps={{
                  translationNamespace: `${Namespace.TeamroomManagement}:actions.requestActivation`,
                  refreshBlock: refresh,
                }}
                {...views[Views.REQUEST_ACTIVATION]}
              />
            )
          : null,
        null,
        true,
        !views[Views.REQUEST_ACTIVATION]?.embedded?.form,
      );

    const InviteButton =
      views[Views.INVITE] &&
      createButtonObject(
        Views.INVITE,
        Icons.ADD_USER_PLUS,
        `${translationNamespace}:actions.invite`,
        DISPLAY_TYPE.COLLAPSIBLE,
        <TeamroomInviteView
          additionalProps={{
            translationNamespace: `${Namespace.TeamroomManagement}:actions.invite`,
          }}
          {...views[Views.INVITE]}
        />,
      );

    const InviteUnregisteredButton =
      views[Views.INVITE_UNREGISTERED] &&
      createButtonObject(
        Views.INVITE_UNREGISTERED,
        Icons.INVITE_UNKNOWN_USER,
        `${translationNamespace}:actions.inviteUnregistered`,
        DISPLAY_TYPE.COLLAPSIBLE,
        <TeamroomInviteView
          additionalProps={{
            translationNamespace: `${Namespace.TeamroomManagement}:actions.inviteUnregistered`,
          }}
          {...views[Views.INVITE_UNREGISTERED]}
        />,
      );

    const LeaveButton =
      views[Views.LEAVE] &&
      createButtonObject(
        Views.LEAVE,
        Icons.LEAVE_TEAMROOM_ARROW,
        `${translationNamespace}:actions.leave`,
        DISPLAY_TYPE.COLLAPSIBLE,
      );

    const JoinButton =
      views[Views.JOIN] &&
      createButtonObject(
        Views.JOIN,
        Icons.ENTER_TEAMROOM_ARROW,
        `${translationNamespace}:actions.join`,
        DISPLAY_TYPE.COLLAPSIBLE,
      );

    const JoinRequestButton =
      views[Views.JOINREQUEST] &&
      createButtonObject(
        Views.JOINREQUEST,
        Icons.ENTER_TEAMROOM_ARROW,
        `${translationNamespace}:actions.joinRequest`,
        DISPLAY_TYPE.COLLAPSIBLE,
      );

    // order in which the buttons will be shown
    return {
      ...InviteButton,
      ...InviteUnregisteredButton,
      ...JoinButton,
      ...JoinRequestButton,
      ...LeaveButton,
      ...RequestActivationButton,
      ...AcceptActivationButton,
      ...RejectActivationButton,
    };
  };

  return (
    <BlockInteractions
      alwaysShowInfo
      views={views}
      activeView={activeView}
      uuid={uuid}
      elementType={elementType}
      instantInteraction={{
        key: Views.INVITATION,
        transnamespace: translationNamespace,
      }}
      buttons={createButtonsObject()}
      info={renderMemberDescription()}
      headline={getHeadline()}
      refreshBlock={refresh}
      refreshCount={refreshCount}
    />
  );
}

TeamroomManagement.propTypes = {
  uuid: PropTypes.string.isRequired,
  elementType: PropTypes.string.isRequired,
  profileLink: LinkShape.isRequired,
  roles: PropTypes.arrayOf(PropTypes.string),
  teamroomMembershipType: PropTypes.string,
  readRestricted: PropTypes.bool,
  activeView: PropTypes.string,
  views: PropTypes.shape({
    requestActivation: PropTypes.shape({
      embedded: PropTypes.shape({
        form: PropTypes.shape({}),
      }),
    }),
    requestActivationApproval: PropTypes.shape({
      embedded: PropTypes.shape({
        profileLinkActivationRequester: LinkShape,
        form: PropTypes.shape({}),
      }),
    }),
    invite: PropTypes.shape({}),
    leave: PropTypes.shape({}),
  }),
  profileLinkActivationRequester: LinkShape,
  refresh: PropTypes.func.isRequired,
  refreshCount: PropTypes.number.isRequired,
};
